const HEART_FRONT_PUBLIC_IDS = [
  "heart-front-1",
  "heart-front-2",
  "heart-front-3",
  "heart-front-4",
  "heart-front-5",
  "heart-front-6",
] as const;

const HEART_FRONT_PUBLIC_STATUSES = [
  "not-connected",
  "unavailable",
  "sealed",
  "exposed",
  "assault-ready",
  "breached",
] as const;

const PROJECTION_KEYS = ["schemaVersion", "revision", "fronts"] as const;
const ROW_KEYS = ["frontId", "status"] as const;
const INVALID_PROJECTION = "invalid-heart-front-public-projection";

export type HeartFrontPublicId = (typeof HEART_FRONT_PUBLIC_IDS)[number];
export type HeartFrontPublicStatus = (typeof HEART_FRONT_PUBLIC_STATUSES)[number];

type Six<T> = readonly [T, T, T, T, T, T];

export interface HeartFrontPublicProjectionRow {
  readonly frontId: HeartFrontPublicId;
  readonly status: HeartFrontPublicStatus;
}

export interface HeartFrontPublicProjection {
  readonly schemaVersion: 1;
  readonly revision: number;
  readonly fronts: Six<HeartFrontPublicProjectionRow>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function hasExactPlainDataShape(
  value: unknown,
  expectedKeys: readonly string[],
): value is Record<string, unknown> {
  try {
    if (!isRecord(value) || Object.getPrototypeOf(value) !== Object.prototype) {
      return false;
    }
    const ownNames = Object.getOwnPropertyNames(value);
    if (
      ownNames.length !== expectedKeys.length ||
      Object.getOwnPropertySymbols(value).length !== 0
    ) {
      return false;
    }
    const expected = new Set(expectedKeys);
    for (const name of ownNames) {
      if (!expected.has(name)) return false;
      const descriptor = Object.getOwnPropertyDescriptor(value, name);
      if (
        descriptor === undefined ||
        !("value" in descriptor) ||
        descriptor.enumerable !== true
      ) {
        return false;
      }
    }
    return expectedKeys.every((key) => ownNames.includes(key));
  } catch {
    return false;
  }
}

function hasExactArrayShape(value: unknown, length: number): value is readonly unknown[] {
  try {
    if (!Array.isArray(value) || value.length !== length) return false;
    const ownNames = Object.getOwnPropertyNames(value);
    if (
      ownNames.length !== length + 1 ||
      Object.getOwnPropertySymbols(value).length !== 0 ||
      !ownNames.includes("length")
    ) {
      return false;
    }
    for (let index = 0; index < length; index += 1) {
      const descriptor = Object.getOwnPropertyDescriptor(value, String(index));
      if (
        descriptor === undefined ||
        !("value" in descriptor) ||
        descriptor.enumerable !== true
      ) {
        return false;
      }
    }
    return true;
  } catch {
    return false;
  }
}

function isNonNegativeSafeInteger(value: unknown): value is number {
  return Number.isSafeInteger(value) && (value as number) >= 0;
}

function isHeartFrontPublicStatus(value: unknown): value is HeartFrontPublicStatus {
  return typeof value === "string" &&
    HEART_FRONT_PUBLIC_STATUSES.includes(value as HeartFrontPublicStatus);
}

function invalidProjection(): never {
  throw new TypeError(INVALID_PROJECTION);
}

export function parseHeartFrontPublicProjection(value: unknown): HeartFrontPublicProjection {
  if (!hasExactPlainDataShape(value, PROJECTION_KEYS) || value.schemaVersion !== 1) {
    return invalidProjection();
  }
  if (!isNonNegativeSafeInteger(value.revision)) return invalidProjection();

  const rows = value.fronts;
  if (!hasExactArrayShape(rows, HEART_FRONT_PUBLIC_IDS.length)) return invalidProjection();

  const fronts = HEART_FRONT_PUBLIC_IDS.map((frontId, index) => {
    const row = rows[index];
    if (
      !hasExactPlainDataShape(row, ROW_KEYS) ||
      row.frontId !== frontId ||
      !isHeartFrontPublicStatus(row.status)
    ) {
      return invalidProjection();
    }
    return Object.freeze({
      frontId,
      status: row.status,
    });
  }) as unknown as Six<HeartFrontPublicProjectionRow>;

  return Object.freeze({
    schemaVersion: 1 as const,
    revision: value.revision,
    fronts: Object.freeze(fronts),
  });
}
